import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {NavStoreRegistry, Registry} from 'constants/RegistryConstants';
import {
  GridSummary,
  MODAL_PANE,
  ModalPane,
  SelectGridMode,
  StorableSummary,
  ValidationErrors,
} from 'constants/SampleConstants';
import NavStore from 'nav/NavStore';
import preferences from 'Preferences';
import {getInitialRegistry, setDefaultRegistry} from 'registry/DefaultRegistry';
import {CreateBoxPaneState, DEFAULT_CREATE_BOX_PANE_STATE} from 'samples/CreateBoxPane';
import {CreatePlatePaneState, DEFAULT_CREATE_PLATE_PANE_STATE} from 'samples/CreatePlatePane';
import {CreateGridPaneState} from 'samples/grids/getCreateGridParams';
import * as StorableActions from 'samples/StorableActions';
import * as StorableModalsUtil from 'samples/StorableModalsUtil';
import {nullableGraphQLStorableSummaryToREST} from 'samples/storables/graphql/translateStorable';
import SelectParentGridPane, {
  DEFAULT_SELECT_PARENT_STORABLE_STATE,
  SelectParentGridPaneState,
  SelectParentStorablePaneState,
} from 'samples/storables/SelectParentGridPane';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import ModalAnchor from 'ui-library/ModalAnchor';
import Modal from 'ui-library/modals/Modal';
import {WorkflowPreferences} from 'workflows/WorkflowConstants';

import * as SampleUtil from '../SampleUtil';
import {SELECT_GRID_MODES} from '../StorableModalsUtil';

type ParentGridPane = ModalPane.CREATE_BOX | ModalPane.CREATE_PLATE | ModalPane.SELECT_PARENT_STORABLE;

function getSelectedPane(
  selectedDestination: SelectGridMode,
  selectedCreateType: CreateType
): ParentGridPane {
  if (selectedDestination === SELECT_GRID_MODES.EXISTING) {
    return ModalPane.SELECT_PARENT_STORABLE;
  } else if (selectedCreateType === 'PLATE') {
    return ModalPane.CREATE_PLATE;
  }
  return ModalPane.CREATE_BOX;
}

function getSelectedPaneState(selectedPane: ParentGridPane, state: State): SelectParentGridPaneState {
  switch (selectedPane) {
    case ModalPane.CREATE_PLATE:
      return state.platePaneState;
    case ModalPane.CREATE_BOX:
      return state.boxPaneState;
    case ModalPane.SELECT_PARENT_STORABLE:
      return state.selectParentPaneState;
    default:
      assert(false, `Unexpected select parent grid pane: ${selectedPane}`);
  }
}

function getValidationErrors(
  paneKey: ParentGridPane,
  paneState: SelectParentGridPaneState
): ValidationErrors {
  switch (paneKey) {
    case ModalPane.CREATE_BOX:
      return StorableModalsUtil.getCreateBoxValidationErrors(paneState as CreateBoxPaneState);
    case ModalPane.CREATE_PLATE:
      return StorableModalsUtil.getCreatePlateValidationErrors(paneState as CreatePlatePaneState);
    case ModalPane.SELECT_PARENT_STORABLE:
      return {};
    default:
      assert(false, `Unexpected select parent grid pane: ${paneKey}`);
  }
}

class SelectParentGridModalConnector extends DataConnector<SelectParentGridModalProps, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [NavStore];

  enhanceProps(): EnhancedProps {
    const registries = this.navStore().getRegistries();
    return {
      registries,
      numRegistries: registries.length,
    };
  }
}

export interface SelectParentGridModalProps {
  mode: SelectGridMode;
  onRequestClose: () => void;
  onRequestSubmit: ({storable}: {storable: StorableSummary | null}) => void;
  storableSchema?: StorableSchema;
  submitStyle?: ButtonStyle;
  submitText?: string;
  submitInProcessText?: string;
}

type CreateType = 'PLATE' | 'BOX';

interface EnhancedProps {
  numRegistries: number;
  registries: Array<NavStoreRegistry>;
}

interface State {
  platePaneState: CreatePlatePaneState;
  boxPaneState: CreateBoxPaneState;
  selectParentPaneState: SelectParentStorablePaneState;
  isCreating: boolean;
  selectedDestination: SelectGridMode;
  selectedCreateType: CreateType;
  initialRegistry: Registry | null;
}

/**
 * Select or create a parent grid, without choosing grid cells.
 */
class SelectParentGridModal extends React.Component<SelectParentGridModalProps & EnhancedProps, State> {
  static defaultProps = {
    submitStyle: ButtonStyle.PRIMARY,
    submitText: 'Save',
    submitInProcessText: 'Saving...',
  };

  constructor(props: SelectParentGridModalProps & EnhancedProps) {
    super(props);
    const selectedDestination =
      this.props.mode !== SELECT_GRID_MODES.CHOOSE
        ? this.props.mode
        : preferences.get(WorkflowPreferences.SELECT_PARENT_GRID_MODAL_SELECTED_DESTINATION) ||
          SelectGridMode.EXISTING;
    const selectedCreateType =
      preferences.get(WorkflowPreferences.SELECT_PARENT_GRID_MODAL_SELECTED_CREATE_TYPE) || 'PLATE';
    const initialRegistry = getInitialRegistry(this.props.registries);

    this.state = {
      selectParentPaneState: {...DEFAULT_SELECT_PARENT_STORABLE_STATE, registry: initialRegistry},
      boxPaneState: {...DEFAULT_CREATE_BOX_PANE_STATE, registry: initialRegistry},
      platePaneState: {...DEFAULT_CREATE_PLATE_PANE_STATE, registry: initialRegistry},
      isCreating: false,
      selectedCreateType,
      selectedDestination,
      initialRegistry,
    };
  }

  componentDidMount(): void {
    if (this.state.initialRegistry?.id) {
      setDefaultRegistry(this.state.initialRegistry?.id);
    }
  }

  _handleUpdatePaneState = (
    selectedPane: ParentGridPane,
    state: Partial<SelectParentGridPaneState>
  ): void => {
    switch (selectedPane) {
      case ModalPane.CREATE_PLATE:
        this.setState({platePaneState: {...this.state.platePaneState, ...(state as CreatePlatePaneState)}});
        break;
      case ModalPane.CREATE_BOX:
        this.setState({boxPaneState: {...this.state.boxPaneState, ...(state as CreateBoxPaneState)}});
        break;
      case ModalPane.SELECT_PARENT_STORABLE:
        this.setState({
          selectParentPaneState: {
            ...this.state.selectParentPaneState,
            ...(state as SelectParentStorablePaneState),
          },
        });
        break;
      default:
        assert(false, `Unexpected select parent grid pane ${selectedPane}`);
    }
  };

  _handleChangeDestination = (value: SelectGridMode): void => {
    this.setState({selectedDestination: value});
    preferences.set(WorkflowPreferences.SELECT_PARENT_GRID_MODAL_SELECTED_DESTINATION, value);
  };

  _handleChangeCreateType = (value: CreateType): void => {
    this.setState({selectedCreateType: value});
    preferences.set(WorkflowPreferences.SELECT_PARENT_GRID_MODAL_SELECTED_CREATE_TYPE, value);
  };

  _getParentStorableSelectorError = (paneState: SelectParentStorablePaneState): string | null => {
    const storable = paneState.selectedParent?.storable;

    const selectedNonGrid = storable && !SampleUtil.isGrid(storable.id);
    const didNotSelectExistingGrid =
      !storable && this.state.selectedDestination === SELECT_GRID_MODES.EXISTING;

    return selectedNonGrid || didNotSelectExistingGrid ? 'Please choose a plate or box to continue.' : null;
  };

  _onCreateSuccess = (grid: GridSummary): void => {
    if (this.props.onRequestSubmit) {
      this.props.onRequestSubmit({storable: grid});
    }
    this.props.onRequestClose();
  };

  private handleCompleteSubmitAttempt = (selectedPane: ParentGridPane, errorMessage?: string): void => {
    if (errorMessage != null) {
      this.setState({isCreating: false});
    }
    const spreadsheetState =
      selectedPane === ModalPane.CREATE_PLATE && errorMessage == null
        ? {spreadsheetFile: null, spreadsheetUrl: null}
        : {};
    const updatedPaneState = {
      isCreating: false,
      validationErrors: errorMessage != null ? {formError: errorMessage} : {},
      ...spreadsheetState,
    };
    this._handleUpdatePaneState(selectedPane, updatedPaneState);
  };

  _handleSubmit = (): void => {
    const {onRequestSubmit} = this.props;
    const selectedPane = getSelectedPane(this.state.selectedDestination, this.state.selectedCreateType);
    const selectedPaneState = getSelectedPaneState(selectedPane, this.state);
    if (selectedPane === MODAL_PANE.SELECT_PARENT_STORABLE) {
      const paneState = selectedPaneState as SelectParentStorablePaneState;
      const {selectedParent} = paneState;
      if (this._getParentStorableSelectorError(paneState)) {
        this._handleUpdatePaneState(selectedPane, {shouldValidateSelectedParent: true});
      } else {
        if (onRequestSubmit) {
          if (selectedParent) {
            onRequestSubmit({
              ...selectedParent,
              storable: nullableGraphQLStorableSummaryToREST(selectedParent.storable),
            });
          } else {
            onRequestSubmit({storable: null});
          }
        }
        this.props.onRequestClose();
      }
    } else {
      const errors = getValidationErrors(selectedPane, selectedPaneState);
      this._handleUpdatePaneState(selectedPane, {validationErrors: errors});
      if (_.isEmpty(errors)) {
        this.setState({isCreating: true});
        StorableActions.createGrid(
          selectedPaneState as CreateGridPaneState,
          (err) => {
            this.handleCompleteSubmitAttempt(selectedPane, err);
          },
          this._onCreateSuccess
        );
      }
    }
  };

  render(): JSX.Element {
    const selectedPane = getSelectedPane(this.state.selectedDestination, this.state.selectedCreateType);
    return (
      <ModalAnchor open={true}>
        <Modal
          title="Choose plate or box"
          onRequestClose={this.props.onRequestClose}
          onRequestSubmit={this._handleSubmit}
          buttons={[
            {
              text: this.state.isCreating ? this.props.submitInProcessText : this.props.submitText,
              submit: true,
              style: this.props.submitStyle,
              disabled: this.state.isCreating,
            },
          ]}
        >
          {this.props.mode === SELECT_GRID_MODES.CHOOSE && (
            <FormGroup>
              <Row>
                <Col md={6}>
                  <FieldLabel isRequired={true} label="Destination" labelStyle={FieldLabel.STYLES.LABEL} />
                  <RadioButtonSelector
                    options={[
                      {value: SELECT_GRID_MODES.EXISTING, label: 'Existing location'},
                      {value: SELECT_GRID_MODES.CREATE, label: 'Create new'},
                    ]}
                    onChange={this._handleChangeDestination}
                    selectedValue={this.state.selectedDestination}
                  />
                </Col>
              </Row>
            </FormGroup>
          )}
          {this.state.selectedDestination === SELECT_GRID_MODES.CREATE && (
            <FormGroup>
              <Row>
                <Col md={6}>
                  <FieldLabel isRequired={true} label="Type" labelStyle={FieldLabel.STYLES.LABEL} />
                  <RadioButtonSelector
                    options={[
                      {value: 'PLATE', label: 'Plate'},
                      {value: 'BOX', label: 'Box'},
                    ]}
                    onChange={this._handleChangeCreateType}
                    selectedValue={this.state.selectedCreateType}
                  />
                </Col>
              </Row>
            </FormGroup>
          )}
          <SelectParentGridPane
            getParentStorableSelectorError={this._getParentStorableSelectorError}
            paneState={getSelectedPaneState(selectedPane, this.state)}
            onUpdate={(state) => {
              this._handleUpdatePaneState(selectedPane, state);
            }}
            numRegistries={this.props.numRegistries}
            registries={this.props.registries}
            selectedPane={selectedPane}
            storableSchema={this.props.storableSchema}
          />
        </Modal>
      </ModalAnchor>
    );
  }
}

export default SelectParentGridModalConnector.connect(SelectParentGridModal);
